const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  try {
    const data = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      // Don't automatically redirect on 401
      // Instead, throw error for the component to handle
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      throw new Error(data.message || `Request failed with status ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('Response handling error:', error);
    throw error; // Preserve the original error
  }
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
};



// api.js - authService section
export const authService = {
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      const data = await handleResponse(response);
      
      return {
        token: data.token,
        user: {
          userId: data.user.userId || data.user.id,
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName
        }
      };
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  },

  verifyToken: async (token) => {
    try {
      if (!token) throw new Error('No token provided');

      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        }
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  }
};



export const projectService = {
  getProjects: async (token) => {
    const response = await fetch(`${API_BASE_URL}/projects`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch projects');
    }
    return handleResponse(response);
  },

  createProject: async (projectName, token) => {
    const response = await fetch(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        project_name: projectName
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create project');
    }
    return handleResponse(response);
  },

  deleteProject: async (projectId, token) => {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete project');
    }
    return response.json();
  },

  uploadFile: async (projectId, file, token) => {
    try {
      // First get the pre-signed URL
      const urlResponse = await fetch(`${API_BASE_URL}/projects/${projectId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
          // Removed projectId from body since it's in the URL path
        })
      });

      if (!urlResponse.ok) {
        const error = await urlResponse.json();
        throw new Error(error.message || 'Failed to get upload URL');
      }

      const { uploadUrl } = await urlResponse.json();

      // Then upload the file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      return true;
    } catch (error) {
      console.error('Error in uploadFile:', error);
      throw error;
    }
  },

  getProjectDetails: async (projectId, token) => {
    try {
      if (!token) {
        throw new Error('No authentication token provided');
      }

      console.log('Making request with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch project details');
      }

      return response.json();
    } catch (error) {
      console.error('Project service error:', error);
      throw error;
    }
  },

  uploadFile: async (projectId, file, token) => {
    // First get the pre-signed URL
    const urlResponse = await fetch(`${API_BASE_URL}/projects/${projectId}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type
      })
    });

    if (!urlResponse.ok) {
      const error = await urlResponse.json();
      throw new Error(error.message || 'Failed to get upload URL');
    }

    const { uploadUrl } = await urlResponse.json();

    // Then upload the file directly to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return true;
  },

  getProjectFileContent: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/files/${fileId}/content?subfolder=${subfolder}`, 
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch file content');
    }
    return response.json();
  },

  deleteProjectFile: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/files/${fileId}?subfolder=${subfolder}`, 
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete file');
    }
    return response.json();
  },

  processProjectFile: async (projectId, fileId, token, subfolder = 'upload') => {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/files/${fileId}/process?subfolder=${subfolder}`, 
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to process file');
    }
    return response.json();
  }

};

export const reportService = {
  getReports: async (token) => {
    const response = await fetch(`${API_BASE_URL}/reports`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch reports');
    return response.json();
  }
};


export const newsletterService = {
  subscribe: async (data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      return handleResponse(response);
    } catch (error) {
      console.error('Newsletter signup error:', error);
      throw error;
    }
  }
};

export const roleService = {
  // Get all users with their roles
  getUsers: async () => {
    console.log('getUsers: Fetching from /users/roles');
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      console.log('getUsers response data:', data);
      return data;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  },

  // Get all roles
  getRoles: async () => {
    console.log('getRoles: Fetching from /roles');
    try {
      const response = await fetch(`${API_BASE_URL}/roles`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      console.log('getRoles response data:', data);
      return data;
    } catch (error) {
      console.error('Get roles error:', error);
      throw error;
    }
  },

  // Create a new role
  createRole: async (roleData) => {
    console.log('createRole: Creating role with data:', roleData);
    try {
      const response = await fetch(`${API_BASE_URL}/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(roleData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create role error:', error);
      throw error;
    }
  },

  // Assign role to user
  assignRole: async (userId, roleId) => {
    console.log(`assignRole: Assigning role ${roleId} to user ${userId}`);
    try {
      const response = await fetch(`${API_BASE_URL}/user/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role_id: roleId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Assign role error:', error);
      throw error;
    }
  },

  // Remove role from user
  removeRole: async (userId, roleId) => {
    console.log(`removeRole: Removing role ${roleId} from user ${userId}`);
    try {
      const response = await fetch(`${API_BASE_URL}/user/roles/${roleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Remove role error:', error);
      throw error;
    }
  }
};

export const articleService = {
  // Get all articles
  getArticles: async (category = 'all') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/articles${category !== 'all' ? `?category=${category}` : ''}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Get articles error:', error);
      throw error;
    }
  },

  // Create new article
  createArticle: async (articleData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/articles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(articleData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create article error:', error);
      throw error;
    }
  },
};


// Subscription Services
export const subscriptionService = {
  createCheckoutSession: async (priceId, userId, userEmail, planId, billingInterval) => {
    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          priceId,
          userId,
          userEmail,
          planId,
          billingInterval
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  },

  // Subscribe to free tier (removing 'api' from path to match other endpoints)
  subscribeFreeTier: async (userId, planId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/free-tier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userId,
          planId
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Free tier subscription error:', error);
      throw error;
    }
  },

  getCurrentSubscription: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${userId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get subscription error:', error);
      throw error;
    }
  },

  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  },

  updateSubscription: async (subscriptionId, updateData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update subscription error:', error);
      throw error;
    }
  },

  getSubscriptionPlans: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get subscription plans error:', error);
      throw error;
    }
  },

  getBillingHistory: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/${userId}/billing-history`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Get billing history error:', error);
      throw error;
    }
  },

  handleWebhookEvent: async (event) => {
    try {
      const response = await fetch(`${API_BASE_URL}/webhooks/stripe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Stripe-Signature': event.signature
        },
        body: JSON.stringify(event.data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Webhook handling error:', error);
      throw error;
    }
  }
};

// Constants for subscription plans
export const SUBSCRIPTION_PLANS = {
  FREE: 'free_tier',
  BASIC: 'basic_tier',
  PREMIUM: 'premium_tier',
  ELITE: 'elite_tier'
};

// Constants for subscription status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  TRIALING: 'trialing'
};

export const portfolioService = {
  updateHoldings: async (holdings) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/portfolio-holdings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ holdings })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update portfolio holdings error:', error);
      throw error;
    }
  },
  
  getHoldings: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      
      // Add error handling and logging
      if (!data) {
        console.log('No data returned from API');
        return [];
      }

      const portfolio_holdings = data.portfolio_holdings || [];
      console.log("portfolio_holdings " + portfolio_holdings)

      // Check if portfolio_holdings exists directly on data
      if (portfolio_holdings.length!=0) {
        return portfolio_holdings;
      }
    
      // If no holdings found, return empty array
      console.log('No portfolio holdings found in response data:', data);
      
      return [];

    } catch (error) {
      console.error('Get portfolio holdings error:', error);
      throw error;
    }
  },

  updateAnalysis: async (analysis) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/portfolio-analysis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ analysis })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Update portfolio analysis error:', error);
      throw error;
    }
  },
  
  getAnalysis: async () => {
    try {
      console.log("fetch(`${API_BASE_URL}/user`")
      const response = await fetch(`${API_BASE_URL}/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      console.log("data.user: " + data.user)
      const analysis = data.user.portfolio_analysis || [];
      console.log("analysis: " + analysis);
      return analysis;
    } catch (error) {
      console.error('Get portfolio analysis error:', error);
      throw error;
    }
  }
};

// Make sure to export the subscriptionService at the end of the file
//export {
 // authService,
  //projectService,
  //reportService,
  //newsletterService,
  ///roleService,
  //articleService,
  //subscriptionService
//};

export const skillsService = {
  // Get user's selected skills from user profile
  getUserSkills: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      console.log('API response data:', data);
  
      // Check if data has a skills property directly or nested in user
      const skillsData = data.skills || (data.user && data.user.skills) || {
        skills: {},
        assets: {},
        relationships: {},
        areas_of_knowledge: {},
        certifications: {},
        degrees: {}
      };
  
      console.log('Extracted skills data:', skillsData);
      return skillsData;
  
    } catch (error) {
      console.error('Get user skills error:', error);
      throw error;
    }
  },

  // Save all skills changes in a single operation
  saveSkills: async (skills, newCustomSkills = []) => {
    try {
      // First update user's selected skills
      const response = await fetch(`${API_BASE_URL}/user/skills`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ skills })
      });
      await handleResponse(response);

      // Then add any new custom skills if provided
      if (newCustomSkills.length > 0) {
        const customResponse = await fetch(`${API_BASE_URL}/skills/custom`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ skills: newCustomSkills })
        });
        await handleResponse(customResponse);
      }

      return true;
    } catch (error) {
      console.error('Save skills error:', error);
      throw error;
    }
  },

  // Get all available skills from skills.json
  getSkillsList: async () => {
    try {
      // Use window.location.origin to get the base URL
      const baseUrl = process.env.PUBLIC_URL || window.location.origin;
      console.log('Attempting to fetch skills.json from:', `${baseUrl}/skills.json`);
      
      const response = await fetch(`${baseUrl}/skills.json`);
      
      if (!response.ok) {
        console.error('Skills list fetch failed:', response.status, response.statusText);
        // Return default structure instead of throwing
        return {
          categories: {
            skills: {
              "Technology": ["Software Development", "Web Development", "Mobile Development"],
              "Business": ["Project Management", "Business Analysis", "Marketing"]
            },
            assets: {},
            relationships: {},
            areas_of_knowledge: {},
            certifications: {},
            degrees: {}
          }
        };
      }

      const data = await response.json();
      console.log('Successfully loaded skills data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching skills.json:', error);
      // Return default structure on error
      return {
        categories: {
          skills: {
            "Technology": ["Software Development", "Web Development", "Mobile Development"],
            "Business": ["Project Management", "Business Analysis", "Marketing"]
          },
          assets: {},
          relationships: {},
          areas_of_knowledge: {},
          certifications: {},
          degrees: {}
        }
      };
    }
  },

  // Search users based on skills and criteria
  searchUsers: async ({ region, state, category, skill, query }) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (region) params.append('region', region);
      if (state) params.append('state', state);
      if (category) params.append('category', category);
      if (skill) params.append('skill', skill);
      if (query) params.append('query', query);

      const response = await fetch(
        `${API_BASE_URL}/users/search?${params.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );

      const data = await handleResponse(response);

      // Transform API response to match the expected format
      return data.users.map(user => ({
        id: user.id,
        name: user.name,
        region: user.region,
        state: user.state,
        skills: user.skills.skills ? 
          Object.values(user.skills.skills)
            .flat()
            .filter(skill => typeof skill === 'string') : [],
        category: category || 'All Categories'
      }));
    } catch (error) {
      console.error('Search users error:', error);
      throw error;
    }
  }
};




//PeerSupportService
export const peerSupportService = {
  // Get user's selected peer support groups
  getUserGroups: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        headers: getAuthHeaders()
      });
      const data = await handleResponse(response);
      
      // Check if data has peer_support_groups property directly or nested in user
      const groups = data.peer_support_groups || 
                    (data.user && data.user.peer_support_groups) || 
                    [];
      
      console.log('Retrieved user peer support groups:', groups);
      return groups;
    } catch (error) {
      console.error('Get user peer support groups error:', error);
      throw error;
    }
  },

  // Save user's selected peer support groups
  saveGroups: async (groups) => {
    try {
      console.log('Saving peer support groups:', groups);
      const response = await fetch(`${API_BASE_URL}/user/peer-support-groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ peer_support_groups: groups })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Save peer support groups error:', error);
      throw error;
    }
  },

  // Remove a specific group from user's selected groups
  removeGroup: async (groupId) => {
    try {
      console.log('Removing peer support group:', groupId);
      const response = await fetch(`${API_BASE_URL}/user/peer-support-groups/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Remove peer support group error:', error);
      throw error;
    }
  },

  // Clear all selected groups
  clearAllGroups: async () => {
    try {
      console.log('Clearing all peer support groups');
      const response = await fetch(`${API_BASE_URL}/user/peer-support-groups/clear`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Clear peer support groups error:', error);
      throw error;
    }
  },

  // Get available peer support groups list
  getAvailableGroups: async () => {
    try {
      // Use window.location.origin to get the base URL
      const baseUrl = process.env.PUBLIC_URL || window.location.origin;
      console.log('Fetching available peer support groups');
      
      const response = await fetch(`${baseUrl}/peersupportgroups.json`);
      
      if (!response.ok) {
        console.error('Peer support groups fetch failed:', response.status, response.statusText);
        throw new Error('Failed to fetch peer support groups');
      }

      const data = await response.json();
      console.log('Successfully loaded peer support groups:', data);
      return data.peerSupportGroups || [];
    } catch (error) {
      console.error('Error fetching peer support groups:', error);
      throw error;
    }
  },

  // Search peer support groups
  searchGroups: async ({ state, searchQuery }) => {
    try {
      // Load all groups and filter client-side
      const groups = await peerSupportService.getAvailableGroups();
      
      return groups.filter(group => {
        const matchesState = !state || group.state.toLowerCase() === state.toLowerCase();
        const matchesSearch = !searchQuery || 
          group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          group.type.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesState && matchesSearch;
      });
    } catch (error) {
      console.error('Search peer support groups error:', error);
      throw error;
    }
  }
};